import { useState } from 'react';
import { authApi, User } from '../services/api';
import { CheckCircle, Shield, Zap, FileCheck, ArrowRight, Eye, Mail, Lock, Upload, Search, Download } from 'lucide-react';
import cloudbyzLogo from '../assets/cloudbyz-logo.png';

function ClosedEye({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={{ transform: 'scaleY(-1)' }}>
      {/* closed eyelid arc */}
      <path d="M2 12c2.5-4 5.5-6 10-6s7.5 2 10 6" />
      {/* lashes */}
      <line x1="12" y1="6"  x2="12" y2="4"  />
      <line x1="7"  y1="7"  x2="5.5" y2="5.5" />
      <line x1="17" y1="7"  x2="18.5" y2="5.5" />
    </svg>
  );
}

interface LoginPageProps {
  onLogin: (user: User) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WORKFLOW_STEPS = [
  { icon: Upload,   label: 'Upload' },
  { icon: Search,   label: 'Validate' },
  { icon: Zap,      label: 'Fix' },
  { icon: Download, label: 'Submit' },
];

const FEATURES = [
  { icon: FileCheck, title: 'Automated Checklist Generation', desc: 'ANDA/NDA checklist generation against FDA requirements' },
  { icon: Shield,    title: 'AI-Powered Validation',          desc: 'Document validation with evidence tracing' },
  { icon: Zap,       title: 'Instant Gap Analysis',           desc: 'Compliance gap analysis and remediation guidance' },
];

type ForgotStep = 'idle' | 'email' | 'code';

export function LoginPage({ onLogin }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [forgotStep, setForgotStep] = useState<ForgotStep>('idle');
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const validateEmail = (val: string) => {
    setEmailError(EMAIL_RE.test(val) ? '' : 'Enter a valid email address');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (emailError) return;
    setError('');
    setLoading(true);
    try {
      const user: User = await authApi.login(email.trim(), password);
      sessionStorage.setItem('auth_user', JSON.stringify(user));
      onLogin(user);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      await authApi.forgotPassword(forgotEmail.trim());
      setForgotStep('code');
    } catch (err: unknown) {
      setForgotError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      await authApi.resetPassword(forgotEmail.trim(), resetCode.trim(), newPassword);
      setResetSuccess(true);
      setTimeout(() => {
        setForgotStep('idle');
        setForgotEmail('');
        setResetCode('');
        setNewPassword('');
        setResetSuccess(false);
      }, 2000);
    } catch (err: unknown) {
      setForgotError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left panel – brand + value prop ── */}
      <div
        className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center gap-8 px-16 py-12 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0F172A 0%, #0D2E5E 60%, #0B4DA6 100%)' }}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-10"
               style={{ background: 'radial-gradient(circle, #007BFF, transparent)' }} />
          <div className="absolute bottom-0 -left-16 w-72 h-72 rounded-full opacity-10"
               style={{ background: 'radial-gradient(circle, #3B82F6, transparent)' }} />
          <svg className="absolute inset-0 w-full h-full opacity-5" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Logo */}
        <div className="relative w-full flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center ring-1 ring-white/20">
            <FileCheck className="w-6 h-6 text-blue-300" />
          </div>
          <span className="text-xl font-bold tracking-tight">AI RegSubmission Agent</span>
        </div>

        {/* Hero text */}
        <div className="relative w-full">
          <h1 className="text-4xl font-bold leading-tight mb-3">
            AI RegSubmission<br />
            <span style={{ color: '#60A5FA' }}>Agent</span>
          </h1>
          <p className="text-base leading-relaxed" style={{ color: 'rgba(148,163,184,0.9)' }}>
            AI-powered FDA submission validation with evidence tracing — all in one seamless workflow.
          </p>
        </div>

        {/* Workflow steps */}
        <div className="relative w-full flex items-center justify-between">
          {WORKFLOW_STEPS.map(({ icon: Icon, label }, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className="flex flex-col items-center gap-2 flex-shrink-0">
                <div className="w-14 h-14 rounded-full flex items-center justify-center ring-2 ring-white/20"
                     style={{ background: 'rgba(59,130,246,0.3)' }}>
                  <Icon className="w-6 h-6" style={{ color: '#93C5FD' }} />
                </div>
                <span className="text-sm font-semibold" style={{ color: 'rgba(203,213,225,0.95)' }}>{label}</span>
              </div>
              {i < WORKFLOW_STEPS.length - 1 && (
                <div className="h-px flex-1 mx-3 mb-6" style={{ background: 'rgba(148,163,184,0.35)' }} />
              )}
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="relative w-full space-y-4">
          {FEATURES.map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5"
                   style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)' }}>
                <Icon className="w-5 h-5" style={{ color: '#93C5FD' }} />
              </div>
              <div>
                <p className="text-sm font-bold leading-tight text-white">{title}</p>
                <p className="text-sm mt-0.5 leading-relaxed" style={{ color: 'rgba(148,163,184,0.85)' }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>

      </div>

      {/* ── Right panel – auth form ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-[#F8FAFC]">
        <div className="w-full max-w-md flex flex-col items-center mb-6">
          <img src={cloudbyzLogo} alt="Cloudbyz" className="h-10 object-contain" />
        </div>

        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8">
            {/* Heading */}
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-foreground">
                {forgotStep !== 'idle' ? 'Reset your password' : 'Sign In'}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {forgotStep === 'email' && "Enter your account email and we'll send you a verification code."}
                {forgotStep === 'code' && 'Check your email for a verification code and enter it below along with your new password.'}
                {forgotStep === 'idle' && 'Welcome back! Please sign in to continue.'}
              </p>
            </div>

            {/* ── Forgot password: enter email ── */}
            {forgotStep === 'email' && (
              <form onSubmit={handleForgotRequest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Work Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      placeholder="you@company.com"
                      required
                      className="w-full pl-9 pr-4 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                    />
                  </div>
                </div>
                {forgotError && (
                  <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <p className="text-sm text-red-700">{forgotError}</p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                >
                  {forgotLoading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sending…</>
                  ) : (
                    <><ArrowRight className="w-4 h-4" />Send Reset Code</>
                  )}
                </button>
                <button type="button" onClick={() => setForgotStep('idle')} className="w-full text-sm text-muted-foreground hover:text-foreground transition text-center">
                  ← Back to sign in
                </button>
              </form>
            )}

            {/* ── Forgot password: enter code + new password ── */}
            {forgotStep === 'code' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                {resetSuccess ? (
                  <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-medium">Password reset! Redirecting to sign in…</p>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">Verification Code</label>
                      <input
                        type="text"
                        value={resetCode}
                        onChange={e => setResetCode(e.target.value)}
                        placeholder="123456"
                        required
                        className="w-full px-4 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition tracking-widest"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">New Password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        className="w-full px-4 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                      />
                    </div>
                    {forgotError && (
                      <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                        <div className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0 flex items-center justify-center mt-0.5">
                          <span className="text-white text-xs font-bold">!</span>
                        </div>
                        <p className="text-sm text-red-700">{forgotError}</p>
                      </div>
                    )}
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm"
                    >
                      {forgotLoading ? (
                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Resetting…</>
                      ) : (
                        <><ArrowRight className="w-4 h-4" />Reset Password</>
                      )}
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-border" />
                      <span className="text-xs text-muted-foreground">or</span>
                      <div className="flex-1 h-px bg-border" />
                    </div>
                    <div className="flex items-center justify-between">
                      <button type="button" onClick={() => setForgotStep('email')} className="text-sm text-primary hover:underline transition">
                        ← Resend code
                      </button>
                      <button type="button" onClick={() => { setForgotStep('idle'); setForgotError(''); }} className="text-sm text-muted-foreground hover:underline transition">
                        Back to Sign In
                      </button>
                    </div>
                  </>
                )}
              </form>
            )}

            {/* ── Login form ── */}
            {forgotStep === 'idle' && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      value={email}
                      onChange={e => { setEmail(e.target.value); if (e.target.value) validateEmail(e.target.value); }}
                      onBlur={e => { if (e.target.value) validateEmail(e.target.value); }}
                      placeholder="you@company.com"
                      required
                      className={`w-full pl-9 pr-4 py-2.5 border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition ${
                        emailError
                          ? 'border-red-400 bg-red-50 focus:ring-red-200'
                          : 'border-border focus:ring-primary/20 focus:border-primary'
                      }`}
                    />
                  </div>
                  {emailError && (
                    <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                      <span className="w-1 h-1 rounded-full bg-red-500" />
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="w-full pl-9 pr-10 py-2.5 border border-border rounded-xl text-sm bg-white placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition"
                      tabIndex={-1}
                    >
                      {showPassword ? <Eye className="w-4 h-4" /> : <ClosedEye className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="flex justify-end mt-1.5">
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(email.trim()); setForgotStep('email'); setForgotError(''); }}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                    <div className="w-4 h-4 rounded-full bg-red-500 flex-shrink-0 flex items-center justify-center mt-0.5">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !!emailError}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white text-sm font-semibold rounded-xl hover:opacity-90 active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-sm mt-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Please wait…
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>

                <p className="text-center text-sm text-muted-foreground pt-1">
                  Need an account? Please contact administrator.
                </p>
              </form>
            )}

          </div>

        </div>

        <p className="text-xs text-muted-foreground mt-6">Powered by CloudByz Inc.</p>
      </div>
    </div>
  );
}
